import { useState, useEffect, useContext, useRef } from "react";
import styles from "./styles.module.css";
import { Grid, Divider } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DataTable from "../utils/DataTable";
import DateTimeHelpers from "../../helpers/DateTimeHelpers";
import CustomerHelper from "helpers/CustomerHelper";
import CustomerService from "services/CustomerService";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import PrinterImg from "images/svg/printer.svg";
import ShareIcon from "images/svg/share.svg";
import Button from "@mui/material/Button";
import useWebInvoicePrint from "helpers/InvoiceHelper";
import ShopContext from "context/ShopContext";
import toast from "react-hot-toast";
import * as SalesHelper from "helpers/SalesHelper";
import { INVOICE_FORMATS } from "const";
import A4Invoice from "components/utils/PrintInvoice/A4Invoice";
import A5Invoice from "components/utils/PrintInvoice/A5Invoice";
import ThreeInchInvoice from "components/utils/PrintInvoice/ThreeInchInvoice";
import * as InvoiceActionHelper from "helpers/InvoiceActionHelper";

function CustomerSaleDialog({ closeDialog, saleId }) {
  const [saleData, setSaleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const printComponentRef = useRef(null);
  const { selectedShop } = useContext(ShopContext);

  const handleWebInvoicePrint = useWebInvoicePrint({
    contentRef: printComponentRef,
    documentTitle: `Invoice-${saleData?.bill_no || "NA"}`,
  });

  const onPrintClick = async () => {
    if (!saleData) return;
    try {
      await SalesHelper.saveAndProcess(saleData, {
        shop: selectedShop,
        fallbackPrintFunction: handleWebInvoicePrint,
      });
    } catch (error) {
      if (error.message && error.message.includes("nodevicefound")) {
        toast.error("No printer found! Added to network queue.");
      } else {
        console.error(error?.message || "Failed to print invoice.");
      }
    }
  };

  const handleShareWhatsApp = () => {
    if (!saleData) return;
    try {
      InvoiceActionHelper.shareInvoiceOnWhatsApp(saleData, selectedShop);
    } catch (error) {
      toast.error(error?.message || "WhatsApp share failed: Customer mobile missing.");
    }
  };

  useEffect(() => {
    if (!saleId) return;

    const fetchSaleDetails = async () => {
      setLoading(true);
      try {
        // 1. Try to fetch from local IndexedDB
        const localSale = await CustomerHelper.getLocalSaleDetails(saleId);
        if (localSale) {
          setSaleData(localSale);
        } else {
          // 2. Fetch from backend API
          const res = await CustomerService.getCustomerSaleDetails(saleId);
          const convertedSale = CustomerHelper.convertServerSaleToLocalSale(res);
          setSaleData(convertedSale);
        }
      } catch (error) {
        console.error("Failed to load sale details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSaleDetails();
  }, [saleId]);

  const items = saleData?.items || [];
  const columnHeader = [
    {
      id: "item.name",
      label: "Name",
      type: "text",
    },
    {
      id: "quantity",
      label: "Quantity",
      type: "floatQuantity",
    },
    {
      id: "rate",
      label: "Price",
      type: "floatAmount",
    },
    {
      id: "tax",
      label: "Tax",
      type: "text",
    },
    {
      id: "payable",
      label: "Total",
      type: "floatAmount",
    },
  ];
  const getDate = (time) => {
    if (!time) return "-";
    const date = new Date(time);
    return DateTimeHelpers.convertDateToUtcDmy(date);
  };
  const getTime = (time) => {
    if (!time) return "-";
    const date = time.split("T")[1].split(".")[0];
    return DateTimeHelpers.formatServerTimeSegment(date);
  };
  const invoiceFormat = SalesHelper.getInvoicePrinterFormat
    ? SalesHelper.getInvoicePrinterFormat()
    : INVOICE_FORMATS.THREE_INCH;

  return (
    <div className={styles.popUp}>
      <Grid
        container
        className={styles.billContentWrapper}
        onClick={(e) => {
          e.stopPropagation();
        }}
        rowSpacing={1.5}
      >
        <Grid item xs={12} mb={2} display="flex" justifyContent="space-between">
          <h3>Sale Details</h3>
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <Button
              className={`${styles.button} ${styles.printBtn}`}
              startIcon={<img src={PrinterImg} alt="Print" className={styles.iconSmall} />}
              disabled={loading || !saleData}
              onClick={onPrintClick}
            >
              Print
            </Button>
            <Button
              className={`${styles.button} ${styles.printBtn}`}
              startIcon={<img src={ShareIcon} alt="Share" className={styles.iconSmall} />}
              disabled={loading || !saleData}
              onClick={handleShareWhatsApp}
            >
              Share
            </Button>
            <CloseRoundedIcon onClick={closeDialog} className={styles.icon} />
          </div>
        </Grid>
        {loading ? (
          <Grid item xs={12} p={2}>
            <SkeletonLoading isOpen={true} count={6} height={40} />
          </Grid>
        ) : (
          <>
            <Grid item xs={12} className={styles.dataContainer}>
              <Grid container rowSpacing={2} columnSpacing={1}>
                <Grid item sm={3} xs={6}>
                  <h5 className={styles.subTitle}>Bill no:</h5>
                  <h5>{saleData?.bill_no}</h5>
                </Grid>
                <Grid item sm={3} xs={6}>
                  <h5 className={styles.subTitle}>Date</h5>
                  <h5>{getDate(saleData?.time)}</h5>
                </Grid>
                <Grid item sm={3} xs={6}>
                  <h5 className={styles.subTitle}>Time</h5>
                  <h5>{getTime(saleData?.time)}</h5>
                </Grid>
                <Grid item sm={3} xs={6}>
                  <h5 className={styles.subTitle}>ID</h5>
                  <h5>{saleData?.id}</h5>
                </Grid>
                <Grid item sm={3} xs={6}>
                  <h5 className={styles.subTitle}>Tax</h5>
                  <h5>{saleData?.tax || 0}</h5>
                </Grid>
                <Grid item sm={3} xs={6}>
                  <h5 className={styles.subTitle}>Additional charges</h5>
                  <h5>{saleData?.cess || 0}</h5>
                </Grid>
                <Grid item sm={3} xs={6}>
                  <h5 className={styles.subTitle}>Discount</h5>
                  <h5 className={styles.heighLite}>{saleData?.discount || 0}</h5>
                </Grid>
                <Grid item sm={3} xs={6}>
                  <h5 className={styles.subTitle}>Payable</h5>
                  <h5 className={styles.heighLite}>{saleData?.payable || 0}</h5>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <h4>Items:</h4>
            </Grid>
            <Grid item xs={12} className={styles.tableContainer}>
              <DataTable columns={columnHeader} rows={items} />
            </Grid>
          </>
        )}
      </Grid>
      {saleData && (
        <div
          style={{
            position: "absolute",
            left: "-10000px",
            top: "-10000px",
            visibility: "hidden",
          }}
        >
          {invoiceFormat === INVOICE_FORMATS.A4 ? (
            <A4Invoice ref={printComponentRef} sale={saleData} shop={selectedShop} />
          ) : invoiceFormat === INVOICE_FORMATS.A5 ? (
            <A5Invoice ref={printComponentRef} sale={saleData} shop={selectedShop} />
          ) : (
            <ThreeInchInvoice ref={printComponentRef} sale={saleData} shop={selectedShop} />
          )}
        </div>
      )}
    </div>
  );
}

export default CustomerSaleDialog;
